import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { css } from 'aphrodite';
import { Tabs, Tab, Box } from '@mui/material';
import TextBase from '../../common/text';
import APIManager from '../../connection/ApiManager';
import { getValueFromObjectByKeys, translate } from '../../helper';
import { Colors } from '../../themes/Colors';
import {
  userRoles,
  WORK_FLOW_LOCATION_ACCOUNT,
  WORK_FLOW_MASTER_ACCOUNT,
} from './const';
import { styles } from './styles';
import { ScreenProviderSubscription, useScreenSubscription } from './screenProviderLocationAccount';
import { WorkFlowSubscription } from './workFlowSubscription';
import { toLower } from 'lodash';
import { SVGImageComponentReact } from '../../themes/Images';
import CustomSpinner from '../../common/CustomSpinner';
import Utils from '../../utils';
import { usePermissions } from '../../hooks/usePermissions';
import PageTitleHeader from '../../common/page-title-header';

const TABS = [
  { id: 0, label: "masterAccountPurchase" },
  { id: 1, label: "locationPurchase" },
];

const brandColor = '#F8B218';

const tabContainerStyle = {
  borderRadius: 'calc(var(--sp-md2)*1.50943)',
  boxShadow: '0 4px 20px rgba(248, 178, 24, 0.08)',
  position: 'relative',
  overflow: 'visible !important',
  width: 'fit-content',
  gap: 'var(--sp-md2)',
  display: 'flex'
};

const getTabStyle = (index, value) => ({
  fontSize: 'calc(var(--fs-xs)*1.4150943)',
  fontWeight: value === index ? 600 : 500,
  textTransform: 'none',
  color: value === index ? '#FFFFFF' : '#6B7280',
  backgroundColor: value === index ? brandColor : '#ECECEC',
  borderRadius: 'calc(var(--sp-md2)*1.132075)',
  padding: 'var(--sp-md2) calc(var(--sp-md2)*1.698113)',
  minHeight: 'calc(var(--fs-hero)*1.226415)',
  position: 'relative',
  maxWidth: 'fit-content',
  overflow: 'visible',
  boxShadow: value === index
    ? '0 6px 16px rgba(248, 178, 24, 0.25)'
    : '0 1px 4px rgba(0, 0, 0, 0.05)',
  transition: 'all 0.25s ease',
  border: 'none',
  '&.Mui-selected': {
    color: '#FFFFFF',
    backgroundColor: brandColor
  },
  '&:hover': {
    backgroundColor: value !== index ? '#E5E7EB' : brandColor,
    transform: 'translateY(-1px)',
  },
  '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 'calc(var(--sp-xxs)*-2)',
        left: '50%',
        transform: 'translateX(-50%)',
        borderLeft: 'var(--sp-md2) solid transparent',
        borderRight: 'var(--sp-md2) solid transparent',
        borderTop: value === index ? `var(--sp-md2) solid ${brandColor}` : 'none',
    }
});

const Subscription = (props) => {
  const { userInfo: { user, profile } = {} } = props;
  const isChildUserAccount = toLower(user?.role) === userRoles.CHILD_ACCOUNT;
  const isChildAccountOrChildTeam = profile?.is_child_account_team;
  const [selectedTab, setSelectedTab] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(false);

  const [masterPlanDetails, setMasterPlanDetails] = useState(null);
  const [childPlanDetails, setChildPlanDetails] = useState(null);
  const [isLoadingPlanDetails, setIsLoadingPlanDetails] = useState(true);

  const [customerSubInfo, setCustomerSubInfo] = useState(null);
  const [isLoadingCustSubInfo, setIsLoadingCustSubInfo] = useState(true);

  const handleTabChange = (_, newTab) => {
    setSelectedTab(newTab);
    if (newTab === TABS[0].id) {
      try { window.__forceMasterMode = true; } catch (e) { }
    }
  };

  const getSubscriptionProducts = async () => {
    try {
      setIsLoadingPlanDetails(true);
      const { data } = await APIManager.getInstance().getSubscitptionProducts();

      const groups = {};
      (data || []).forEach(item => {
        const rawName = item?.name || 'unknown';
        const key = rawName.toLowerCase().trim();
        if (!groups[key]) {
          groups[key] = { displayName: rawName, monthly: null, yearly: null, raw: [] };
        }
        groups[key].raw.push(item);
        if (item.recurring_interval === 1) {
          groups[key].monthly = item;
        } else if (item.recurring_interval === 2) {
          groups[key].yearly = item;
        }
      });

      setMasterPlanDetails(groups);
      setChildPlanDetails(groups);
    } catch (error) {
      console.error("Error fetching subscription product: ", error);
    } finally {
      setIsLoadingPlanDetails(false);
    }
  };

  const getCustomerSubInfo = async () => {
    try {
      setIsLoadingCustSubInfo(true);
      const api = APIManager.getInstance();
      const { data } = await (isChildAccountOrChildTeam
        ? api.getVendorSubscitption()
        : api.getVendorAllSubscitption());

      setCustomerSubInfo(data ?? null);
    } catch (error) {
      console.error("Error fetching Vendor Account: ", error);
      Utils.showAlert(2, error.detail || error.message);
      setCustomerSubInfo(null);
    } finally {
      setIsLoadingCustSubInfo(false);
    }
  };

  useEffect(() => {
    getCustomerSubInfo();
    getSubscriptionProducts();
  }, [refreshTrigger]);

  const location = useLocation();
  useEffect(() => {
    if (location?.state?.initialTab === 'location') {
      setSelectedTab(TABS[1].id);
      try {
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (e) {
      }
    }
  }, [location?.state?.initialTab]);

  useEffect(() => {
    if (!(location?.state?.initialTab === 'location')) {
      setSelectedTab(TABS[0].id);
      try { window.__forceMasterMode = true; } catch (e) { }
    }
  }, [location.key]);

  const initialScreenFromNav = location?.state?.initialScreen || null;

  const getProviderInitialScreen = () => {
    if (selectedTab === TABS[0].id) {
      const isActivePlan = customerSubInfo?.subscription?.subscription?.is_active || customerSubInfo?.subscription?.is_active || false;
      return isActivePlan ? WORK_FLOW_MASTER_ACCOUNT.ACTIVE_MAIN_SUBSCRIPTION : WORK_FLOW_MASTER_ACCOUNT.MASTER_ACCOUNT_SUBSCRIPTION;
    }

    if (selectedTab === TABS[1].id) {
      return initialScreenFromNav || WORK_FLOW_LOCATION_ACCOUNT.ACTIVE_LOCATION_SUBSCRIPTION;
    }

    return WORK_FLOW_MASTER_ACCOUNT.ACTIVE_MAIN_SUBSCRIPTION;
  };

  const providerKey = getProviderInitialScreen();
  const SubscriptionInner = ({ }) => {
    const { screen, goBack } = useScreenSubscription();
    const [isLocationFlow, setIsLocationFlow] = useState(false);
    const {canEdit} = usePermissions();
    const canEditSubscriptions = canEdit('subscriptions');

    useEffect(() => {
      if (screen === WORK_FLOW_LOCATION_ACCOUNT.LOCATION_ACCOUNT_SUBSCRIPTION) {
        setIsLocationFlow(true);
      } else {
        setIsLocationFlow(false);
      }
    }, [screen]);
    const Header = () => {
      if (!isLocationFlow) {
        return (
          <PageTitleHeader
            title={translate('subscription')}
          />
        );
      }

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: "calc(var(--sp-md2)*1.132)" }}>
          <div className="cursor-pointer" onClick={goBack}>
            <SVGImageComponentReact.IconArrowBack style={{width:"var(--fs-lg)", height:"var(--fs-lg)", color: '#1A5A78'}}/>
          </div>
          <PageTitleHeader title={translate('createLocation') || 'Create Location'} showBar={false} />
        </div>
      );
    };
    return (
      <>
        <Header />

        <div className={css(styles.tabContainer)}>
          <Box style={{ width: "100%" }}>
            {isChildAccountOrChildTeam ? (
              customerSubInfo == null ? (
                <p>{translate('noSubscriptionData')}</p>
              ) : (
                <div>
                  <WorkFlowSubscription
                    customerSubInfo={customerSubInfo}
                    subscriptionProduct={childPlanDetails}
                  />
                </div>
              )
            ) : (
              <>
                {!isLocationFlow && (
                  canEditSubscriptions ?
                    <Tabs
                      value={selectedTab}
                      onChange={handleTabChange}
                      sx={{
                        overflow: "visible",
                        '& .MuiTabs-scroller': { overflow: "visible !important" },
                        '& .MuiTabs-flexContainer': tabContainerStyle
                    }}
                    TabIndicatorProps={{ style: { display: 'none' } }}
                    >
                      {customerSubInfo?.subscription !== null && customerSubInfo?.subscription !== undefined ? (
                        TABS.map((tab) => (
                          <Tab
                            key={tab.id}
                            label={translate(tab.label)}
                            sx={getTabStyle(tab.id, selectedTab)}
                          />
                        ))
                      ) : (
                        <Tab
                          key={TABS[0].id}
                          label={translate(TABS[0].label)}
                          sx={getTabStyle(TABS[0].id, selectedTab)}
                        />
                      )}
                    </Tabs>
                  : <></>
                )}

                <div>
                  <WorkFlowSubscription
                    customerSubInfo={customerSubInfo}
                    subscriptionProduct={selectedTab === TABS[0].id ? masterPlanDetails : childPlanDetails}
                  />
                </div>
              </>
            )}
          </Box>
        </div>
      </>
    );
  };


  return (
    <div className={css(styles.container)}>
      {(isLoadingCustSubInfo || isLoadingPlanDetails) ? (
        <CustomSpinner fullPage />
      ) : (
        <ScreenProviderSubscription
          key={providerKey}
          initialScreen={providerKey}
          triggerRefresh={() => setRefreshTrigger(prev => !prev)}
          user={user}
          profile={profile}
        >
          <SubscriptionInner />
        </ScreenProviderSubscription>
      )}
    </div>
  );
  
};

const mapStateToProps = (state) => {
  return {
    userInfo: getValueFromObjectByKeys(state, ['User', 'userInfo']),
  };
};

export default connect(mapStateToProps)(Subscription);